import React, { useEffect, useState } from "react";
import HeaderContainer from "./Header";
import { env, useGlobalState } from "../helpers/GlobalStateProvider";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiUrls, getApiUrl } from "../config";
import { Card, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { Button } from "../components/ui/button";

const SignupConfirm: React.FC<any> = () => {
  const navigate = useNavigate();
  const { info, handleLogout } = useGlobalState();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Determine if email still needs verification
  const emailNeedsVerify = !(info?.emailVerified);

  useEffect(() => {
    if (!emailNeedsVerify) {
      // If the email is already verified, redirect to the dashboard
      navigate("/dashboard");
    }
  }, [emailNeedsVerify, navigate]);

  const handleResendEmail = async () => {
    if (!info?.email) {
      if (process.env.NODE_ENV === 'development') console.log("Missing user information", info);
      setMessage("Missing user information");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/resend_email`,
        { email: info.email },
        {
          headers: {
            "Content-Type": "application/json",
            // Add an Authorization header if needed:
            // Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      setMessage("Verification email resent successfully!");
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error resending email:", error);
      setMessage("Error resending email. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOver = async () => {
    try {
      await handleLogout();
      // handleLogout already navigates to /signin, but we want to go to /signup
      navigate("/signup");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error starting over:", error);
      // If logout fails, still redirect to signup
      navigate("/signup");
    }
  };

  return (
    <Flex
      minHeight="100vh"
      width="100%"
      maxWidth="1392px"
      margin="0 auto"
      paddingTop="24px"
      paddingBottom="24px"
      direction="column"
      gap="24px"
    >
      {/* Header */}
      <HeaderContainer behavior="confirm_email" />

      {/* Main container to center the content */}
      <Flex flex={1} justifyContent="center" alignItems="center" padding="20px">
        {/* Content wrapper for max width */}
        <Stack maxWidth="480px" width="100%" alignItems="center" gap={8}>
          {/* Page title */}
          <Heading
            as="h1"
            textAlign="center"
            color="#18181b"
            fontSize="24px"
            fontWeight="bold"
            letterSpacing="-0.4px"
            lineHeight="32px"
          >
            Confirm your email address
          </Heading>

          {/* Confirm Email Card */}
          <Card.Root width="100%">
            <Card.Body>
              <Stack gap={4} alignItems="center" textAlign="center">
                <Text fontSize="md" color="#52525b" lineHeight="24px">
                  Click the confirm link in your email to verify your address and complete account creation.
                </Text>

                {/* Resend email confirmation link (styled as a button link) */}
                <Button
                  onClick={handleResendEmail}
                  disabled={isLoading}
                  variant="plain"
                  color="#9333ea"
                  fontWeight="semibold"
                  textDecoration="underline"
                >
                  {isLoading ? "Resending..." : "Resend email confirmation"}
                </Button>

                {message && (
                  <Text fontSize="sm" color="#9333ea">
                    {message}
                  </Text>
                )}

                {/* Start Over button */}
                <Button onClick={handleStartOver} variant="outline" size="sm">
                  Start over with different email
                </Button>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Stack>
      </Flex>
    </Flex>
  );
};

export default SignupConfirm;
